import { AuthContext } from '../../context/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { useContext, useState, useRef, useEffect } from 'react';
import { useLang } from '../../context/LangContext';

import logo from '../../assets/logos/cetificated.png';
import esTexts from '../../translations/es.json';
import enTexts from '../../translations/en.json';

const Header = () => {
  const { token, currentUser, setUserLogout } = useContext(AuthContext);
  const { lang, setLang } = useLang();
  const location = useLocation();
  const texts = lang === 'es' ? esTexts : enTexts;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => setDropdownOpen(prev => !prev);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setDropdownOpen(false);
  }, [location.pathname]);

  const handleChangeLang = newLang => {
    setLang(newLang);
  };

  const logout = () => {
    setUserLogout();
    window.location.href = '/';
  };

  // Get user avatar initials or fallback
  const getUserInitial = () => {
    if (currentUser?.fullname) return currentUser.fullname.charAt(0).toUpperCase();
    if (currentUser?.email) return currentUser.email.charAt(0).toUpperCase();
    return 'U';
  };

  return (
    <div className="bg-sen p-3 header-border">
      <div className="container d-flex justify-content-between align-items-center p-0">
        {/* Brand / Logo */}
        <div className="d-flex align-items-center">
          <Link to="/" className="text-decoration-none d-flex align-items-center">
            <img
              src={logo}
              alt="Logo"
              className="rounded-circle"
              style={{ width: '48px', height: '48px' }}
            />
            <span
              className="text-white ms-3 header-title-mobile"
              style={{ fontSize: '1.2rem', letterSpacing: '0.5px' }}
            >
              <span style={{ fontWeight: '300' }}>ALOJAMIENTOS</span>{' '}
              <span style={{ fontWeight: 'bold' }}>CERTIFICADOS</span>
            </span>
          </Link>
        </div>

        {/* Right Navigation Controls */}
        <div className="d-flex align-items-center gap-2">
          {/* Minimalist Language Switcher Pill */}
          {!window.location.pathname.includes('admin') && (
            <div className="header-lang-switch">
              <button
                type="button"
                className={`header-lang-btn ${lang === 'es' ? 'active' : ''}`}
                onClick={() => handleChangeLang('es')}
                title="Español"
              >
                ES
              </button>
              <button
                type="button"
                className={`header-lang-btn ${lang === 'en' ? 'active' : ''}`}
                onClick={() => handleChangeLang('en')}
                title="English"
              >
                EN
              </button>
            </div>
          )}

          {/* User Section */}
          {!token ? (
            <Link
              to="/login"
              className="btn btn-sm btn-outline-light rounded-pill px-3 py-1-5 ms-1 d-flex align-items-center gap-2 text-decoration-none fw-semibold shadow-xs"
              style={{ fontSize: '0.85rem' }}
            >
              <i className="fa fa-user-circle-o" style={{ fontSize: '1rem' }}></i>
              <span>{lang === 'es' ? 'Iniciar Sesión' : 'Log In'}</span>
            </Link>
          ) : (
            <>
              {/* Direct Access Badge for University Accommodations */}
              {currentUser?.universitySlug && (
                <Link
                  to={`/page/${currentUser.universitySlug}`}
                  className="header-nav-badge-btn"
                  title={texts.nav_accommodations}
                >
                  <i className="fa fa-building"></i>
                  <span className="d-none d-md-inline">
                    {texts.nav_accommodations || 'Alojamientos'}
                  </span>
                </Link>
              )}

              {/* User Dropdown Menu (Option A - Circular Avatar Only) */}
              <div className="position-relative d-inline-flex align-items-center" ref={dropdownRef}>
                <button
                  type="button"
                  className="header-user-avatar-btn"
                  onClick={toggleDropdown}
                  aria-expanded={dropdownOpen}
                  title={currentUser?.fullname || currentUser?.email || 'Mi Cuenta'}
                >
                  <span>{getUserInitial()}</span>
                </button>

                {/* Dropdown Menu Box */}
                {dropdownOpen && (
                  <div className="header-dropdown-menu">
                    {/* User Info Header in Dropdown */}
                    <div className="px-3 py-2 border-bottom mb-1 bg-light rounded-top">
                      <div className="fw-bold text-dark small text-truncate">
                        {currentUser?.fullname || currentUser?.email}
                      </div>
                      <div className="text-muted text-truncate" style={{ fontSize: '0.725rem' }}>
                        {currentUser?.isAdmin
                          ? lang === 'es'
                            ? 'Administrador'
                            : 'Administrator'
                          : currentUser?.universitySlug || currentUser?.email}
                      </div>
                    </div>

                    <Link to="/profile" className="header-dropdown-item">
                      <i className="fa fa-user-o"></i>
                      <span>{texts.nav_profile || (lang === 'es' ? 'Mi Perfil' : 'Profile')}</span>
                    </Link>

                    <Link to="/admin" className="header-dropdown-item">
                      <i className="fa fa-gear"></i>
                      <span>
                        {currentUser?.isAdmin
                          ? texts.admin?.title_list ||
                            (lang === 'es' ? 'Panel de Administración' : 'Admin Panel')
                          : lang === 'es'
                            ? 'Panel de Control'
                            : 'Control Panel'}
                      </span>
                    </Link>

                    <div className="header-dropdown-divider"></div>

                    <button
                      type="button"
                      className="header-dropdown-item danger-item"
                      onClick={logout}
                    >
                      <i className="fa fa-sign-out"></i>
                      <span>{lang === 'es' ? 'Cerrar Sesión' : 'Log Out'}</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
